import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  SystemStyleObject,
} from "@chakra-ui/react";
import {
  getCeleryWorkflowStatus,
  useDownloadEventTemplate,
} from "features/internal/template/utils";
import {
  useInternalContinueEventLoadCreate,
  useInternalEventImportExportLoadStatsRetrieveSuspense,
  useInternalEventImportExportRetrieveSuspense,
  useInternalEventTemplateEventTableDataRetrieveSuspense,
  useInternalEventTemplateVersionRetrieve,
  useInternalLoadEventCheckpointStatusesRetrieveSuspense,
  useInternalLoadEventCheckpointUpdate,
} from "gen/endpoints/internal/internal";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { compact, noop } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  useViewEditDropdown,
  ViewEditDropdownButton,
} from "shared/components/button/ViewEditDropdown";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  GetLinkInfoForRowFn,
  RowSelectionState,
} from "shared/components/table/data/DataTableTypes";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";

const tableSx: SystemStyleObject = {
  ".ag-row.ag-row-selected": {
    "--ag-background-color": "var(--chakra-colors-primary-50)",
    "--ag-odd-row-background-color": "var(--chakra-colors-primary-50)",
    ".ag-checkbox-input-wrapper": {
      borderColor: "primary.700",
      backgroundColor: "primary.25",
      _after: {
        bgColor: "primary.700",
      },
    },
  },
};

const InternalTemplateLoadCheckpointFormTable: React.FC<{
  setEditMode: (editMode: boolean) => void;
}> = ({ setEditMode }) => {
  const { t } = useTranslation(["common", "internal"]);
  const { templateVersionId, templateImportExportId } =
    useRequiredStringParams();
  const form = useForm();

  const { data: eventCheckpointStatusData } =
    useInternalLoadEventCheckpointStatusesRetrieveSuspense(
      templateImportExportId,
    );
  const { mutateAsync: setLoadEventCheckpoints } =
    useInternalLoadEventCheckpointUpdate();

  const { data: tableData, refetch: refetchTableData } =
    useInternalEventTemplateEventTableDataRetrieveSuspense(templateVersionId, {
      event_import_export_id: templateImportExportId,
      edit_mode: "set_checkpoints",
      exclude_draft_events: false,
    });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    eventCheckpointStatusData.load_event_id_to_checkpoint_status,
  );

  return (
    <Form
      form={form}
      onSubmit={async () => {
        await setLoadEventCheckpoints({
          eventImportExportId: templateImportExportId,
          data: {
            load_event_id_to_checkpoint_status: rowSelection,
          },
        });
      }}
      onSuccess={async () => {
        await refetchTableData();
        setEditMode(false);
      }}
      successMessage={t(
        "internal:templateTabs.loadTemplate.setCheckPoints.success",
      )}
    >
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("internal:templateTabs.loadTemplate.setCheckPoints.title")}
          </TableBoxTitle>
          <DataTableBoxActions showExport={false}>
            <ViewEditDropdownButton
              isEditing={true}
              setIsEditing={setEditMode}
            />
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tableData}
            showAllRows={true}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            sx={tableSx}
          />
        </TableBoxBody>
      </TableBox>
      <FormFooter>
        <FormFooterActions>
          <FormCancelButton onClick={() => setEditMode(false)} />
          <FormSubmitButton>{t("common:save")}</FormSubmitButton>
        </FormFooterActions>
      </FormFooter>
    </Form>
  );
};

const InternalTemplateLoadTable: React.FC<{
  templateVersionId: string;
  templateImportExportId: string;
  editModeEnabled: boolean;
  setEditMode: (editMode: boolean) => void;
}> = ({
  templateVersionId,
  templateImportExportId,
  editModeEnabled,
  setEditMode,
}) => {
  const { t } = useTranslation(["common", "internal"]);

  const downloadEventTemplate = useDownloadEventTemplate(templateVersionId);

  const { data: importExportData, refetch: refetchImportExportData } =
    useInternalEventImportExportRetrieveSuspense(templateImportExportId);

  const {
    mutateAsync: loadTemplateData,
    isPending: isLoadTemplateDataPending,
  } = useInternalContinueEventLoadCreate({
    mutation: {
      onSuccess: () => refetchImportExportData(),
    },
  });

  const { data: loadStats, refetch: refetchLoadStats } =
    useInternalEventImportExportLoadStatsRetrieveSuspense(
      templateImportExportId,
    );

  const { data: tableData, refetch: refetchTableData } =
    useInternalEventTemplateEventTableDataRetrieveSuspense(templateVersionId, {
      event_import_export_id: templateImportExportId,
      edit_mode: "set_checkpoints",
      exclude_draft_events: false,
    });

  const { data: progressTableData, refetch: refetchProgressTableData } =
    useInternalEventTemplateEventTableDataRetrieveSuspense(templateVersionId, {
      event_import_export_id: templateImportExportId,
      edit_mode: "set_checkpoints",
      exclude_draft_events: true,
    });

  const onProcessing = useCallback(async () => {
    await Promise.allSettled([
      refetchTableData(),
      refetchProgressTableData(),
      refetchLoadStats(),
    ]);
  }, [refetchTableData, refetchProgressTableData, refetchLoadStats]);

  const updateConfig = useMemo(
    () => ({
      onProcessing,
      allowedInterval: 5000,
    }),
    [onProcessing],
  );
  const workflowStatuses = useCeleryWorkflowPoll(
    compact([importExportData.celery_workflow_id]),
    noop,
    onProcessing,
    onProcessing,
    updateConfig,
  );

  const workflowStatus = getCeleryWorkflowStatus(
    importExportData.celery_workflow_id,
    workflowStatuses,
  );

  const isWorkflowInProgress =
    workflowStatus?.state === CeleryWorkflowStateMachineFieldsEnum.processing ||
    isLoadTemplateDataPending;
  const isWorkflowError =
    workflowStatus?.state === CeleryWorkflowStateMachineFieldsEnum.failed;
  const workflowErrorMessage = workflowStatus?.error_message;

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) =>
      row.data?.metadata?.type === "id_row" &&
      !editModeEnabled &&
      !isWorkflowInProgress
        ? {
            type: "generic",
            to: rootRoute({})
              .internal({})
              .shadowTemplate({ templateVersionId })
              .templateEvent({
                templateEventId: row.data.metadata.id,
              }).$,
          }
        : undefined,
    [editModeEnabled, templateVersionId, isWorkflowInProgress],
  );

  const getTableButtons = () => {
    if (
      workflowStatus?.state === CeleryWorkflowStateMachineFieldsEnum.complete &&
      loadStats.completed_events === loadStats.total_events
    ) {
      return (
        <Button size="xs" onClick={downloadEventTemplate}>
          {t("internal:downloadTemplate")}
        </Button>
      );
    }

    if (loadStats.completed_events === 0) {
      return (
        <Button
          size="xs"
          isLoading={isWorkflowInProgress}
          onClick={async () => {
            await loadTemplateData({
              eventImportExportId: importExportData.id,
            });
          }}
        >
          {t("internal:loadTemplateData.startLoad")}
        </Button>
      );
    } else if (
      workflowStatus?.state === CeleryWorkflowStateMachineFieldsEnum.failed
    ) {
      return (
        <Button
          size="xs"
          colorScheme="destructive"
          isLoading={isWorkflowInProgress}
          onClick={async () => {
            await loadTemplateData({
              eventImportExportId: importExportData.id,
            });
          }}
        >
          {t("internal:loadTemplateData.retryLoad")}
        </Button>
      );
    } else {
      return (
        <Button
          size="xs"
          colorScheme="tertiary"
          isLoading={isWorkflowInProgress}
          onClick={() =>
            loadTemplateData({ eventImportExportId: importExportData.id })
          }
        >
          {t("internal:loadTemplateData.continueLoad")}
        </Button>
      );
    }
  };

  return (
    <>
      {isWorkflowError && (
        <Alert
          data-ls-enabled={true}
          data-public={true}
          status="error"
          margin="8px 0px 8px 0px"
        >
          <AlertIcon>
            <InfoCircleIcon />
          </AlertIcon>
          <AlertTitle flexGrow="0">{t("common:error.generic")}</AlertTitle>
          <AlertDescription>{workflowErrorMessage}</AlertDescription>
        </Alert>
      )}
      <TableBox>
        <TableBoxStats>
          <StatGroup justifyContent="flex-start">
            {loadStats.stats.map((stat, i) => (
              <Stat size="xs" key={`overall_stat_${i}`}>
                <StatLabel>{stat.rich_text}</StatLabel>
                <StatNumber>{stat.value}</StatNumber>
              </Stat>
            ))}
          </StatGroup>
        </TableBoxStats>
        <TableBoxHeader>
          <TableBoxTitle>{t("common:events")}</TableBoxTitle>
          <DataTableBoxActions showExport={false}>
            {loadStats.completed_events === 0 && (
              <ViewEditDropdownButton
                isEditing={editModeEnabled}
                setIsEditing={setEditMode}
              />
            )}
            {getTableButtons()}
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={isWorkflowInProgress ? progressTableData : tableData}
            getLinkInfoForRow={getLinkInfoForRow}
            isLoading={isWorkflowInProgress}
            showAllRows={true}
          />
        </TableBoxBody>
      </TableBox>
    </>
  );
};

export const InternalTemplateLoadPage: React.FC = () => {
  const { t } = useTranslation(["common", "internal"]);
  const { templateVersionId, templateImportExportId } =
    useRequiredStringParams();
  const { editModeEnabled, setEditMode } = useViewEditDropdown();

  const { data: importExportData } =
    useInternalEventImportExportRetrieveSuspense(templateImportExportId);

  const { data: templateData } = useInternalEventTemplateVersionRetrieve(
    importExportData.event_template_version_id,
    {
      query: {
        enabled: !!importExportData,
      },
    },
  );

  return (
    <PageContent>
      {templateData && (
        <PageHeader
          title={templateData.name}
          helperText={templateData.description}
          backButton={{
            title: t("common:back"),
          }}
        />
      )}
      <PageBody>
        {editModeEnabled ? (
          <InternalTemplateLoadCheckpointFormTable setEditMode={setEditMode} />
        ) : (
          <InternalTemplateLoadTable
            templateVersionId={templateVersionId}
            templateImportExportId={templateImportExportId}
            editModeEnabled={editModeEnabled}
            setEditMode={setEditMode}
          />
        )}
      </PageBody>
    </PageContent>
  );
};
